/** Màn Hôm nay — `S.today` của bản mẫu (spec 01): hero, kế hoạch tối nay, thẻ tối "Hôm nay của bạn", thói quen. */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { DaySummary, RevisionStats, type Account } from "@ca/shared";
import { Button, ErrorState, Fish, Ring, Skeleton, SpaceTag } from "@ca/ui";
import { api } from "./api.ts";
import { useRead } from "./revision-ui.tsx";
import { useToday } from "./productivity.tsx";
import { useDailyRounds, useFocus } from "./focus.tsx";
import { useCalendar, targetFor } from "./planner.tsx";
import { dayLabel, useActivity } from "./day-end.tsx";
import { NewBadge } from "./flags.tsx";
import { ACT, fmtMin } from "./charts.tsx";

const SKILL_ICON: Record<string, string> = {
  nghe: "🎧",
  doc: "📖",
  thithu: "📝",
  nguphap: "📚",
  tuvung: "📗",
};
const SKILL_NAME: Record<string, string> = {
  nghe: "Luyện nghe",
  doc: "Luyện đọc",
  thithu: "Đề thi thử",
  nguphap: "Ngữ pháp",
  tuvung: "Từ vựng",
};
export const hhmm = (n: number) =>
  `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;

/** Lỗi làm mất điểm nhiều nhất, cho bong bóng lời nói của Cá. */
function insight(stats: RevisionStats | undefined) {
  const trap = [...(stats?.traps ?? [])].sort((a, b) => b.wrong - a.wrong)[0];
  if (trap?.wrong)
    return `Cá soi thấy bẫy “${trap.title}” đang làm bạn mất điểm nhất. Tối nay mình trị nó nhé!`;
  const skill = [...(stats?.skills ?? [])].sort((a, b) => b.wrong - a.wrong)[0];
  if (skill?.wrong)
    return `Cá soi thấy dạng “${skill.title}” đang làm bạn mất điểm nhất. Tối nay mình trị nó nhé!`;
  return null;
}

export function TodayPage({
  account,
  notify,
}: {
  account: Account;
  notify: (text: string) => void;
}) {
  const uid = account.id,
    q = useToday(uid),
    navigate = useNavigate(),
    focus = useFocus(uid),
    dailyRounds = useDailyRounds(),
    stats = useRead(uid, "/v1/me/stats/summary", RevisionStats);
  const date = q.data?.date ?? "",
    plan = useCalendar(uid, date || "1970-01-01", date || "1970-01-01"),
    activity = useActivity(uid, date, date, !!date),
    day = useQuery({
      queryKey: ["day-summary", uid, date],
      enabled: !!date,
      queryFn: async () =>
        DaySummary.parse(await api(`/v1/me/day-summaries/${date}`, { accountId: uid })),
      refetchInterval: 30000,
    });
  const [saving, setSaving] = useState(false);
  const go = (path: string) => {
    void navigate({ to: "/$", params: { _splat: path.replace(/^\//, "") } });
  };
  if (q.isPending) return <Skeleton />;
  if (q.isError)
    return (
      <ErrorState
        message={String(q.error)}
        onRetry={() => {
          void q.refetch();
        }}
      />
    );
  const t = q.data;
  const name = account.profile.displayName ?? account.name ?? "bạn";
  const minutes = Math.floor(t.activeSecs / 60),
    goal = t.goalMinutes,
    left = Math.max(0, goal - minutes),
    reached = minutes >= goal;
  const items = [...(plan.data ?? [])].sort((a, b) => a.startMin - b.startMin);
  const planMinutes = items.reduce((n, e) => n + e.endMin - e.startMin, 0);
  const firstOpen = items.findIndex((e) => !e.done);
  const streak = t.streak;
  const speech = reached
    ? "Đạt mục tiêu ngày rồi! Xem tổng kết để thấy hôm nay bạn đã làm được gì nhé."
    : insight(stats.data);
  const practice = `/practice/${t.nextPart <= 4 ? "listening" : "reading"}/${t.nextPart}`;
  const hourly: Record<number, number> = {};
  for (const b of activity.data?.buckets ?? [])
    hourly[b.hour] = (hourly[b.hour] ?? 0) + b.activeSecs / 60;
  const answered = day.data?.answered ?? 0,
    correct = day.data?.correct ?? 0;
  const start = () => {
    const first = items[firstOpen];
    go(first ? targetFor(first.skill) : practice);
    window.dispatchEvent(new Event("ca-focus-open"));
  };
  return (
    <>
      <section className="hero">
        <div>
          <div className="row">
            <span className="space-tag common">
              {dayLabel(t.date).replace(/ (\d\d\/\d\d)$/, " · $1")}
            </span>
          </div>
          <h1 style={{ marginTop: 12 }}>Chào {name}. Cá đây nè 👋</h1>
          <p className="lead">
            {reached ? (
              <>
                Hôm nay bạn đã học <b>{fmtMin(minutes)}</b>, vượt mục tiêu {minutes - goal} phút.
                {streak ? (
                  <>
                    {" "}
                    Chuỗi <b>{streak} ngày</b> 🔥
                  </>
                ) : null}{" "}
                Bấm <b>Kết thúc hôm nay</b> để xem tổng kết.
              </>
            ) : items.length ? (
              <>
                Tối nay có{" "}
                <b>
                  {items.length} việc · {planMinutes} phút
                </b>{" "}
                trong lịch. Bạn đã có{" "}
                <b>
                  {minutes}/{goal} phút
                </b>{" "}
                từ sáng, học thêm <b>{left} phút</b> là {streak ? "giữ" : "bắt đầu"} chuỗi{" "}
                <b>{Math.max(1, streak)} ngày</b>.
              </>
            ) : (
              <>
                Tối nay chưa có lịch. Bạn đã học{" "}
                <b>
                  {minutes}/{goal} phút
                </b>
                , học thêm <b>{left} phút</b> là {streak ? "giữ" : "bắt đầu"} chuỗi{" "}
                <b>{Math.max(1, streak)} ngày</b>.
              </>
            )}
          </p>
          <div className="cta">
            <Button size="lg" onClick={start}>
              ▶ Bắt đầu buổi học <NewBadge />
            </Button>
            <Button
              variant="acc"
              size="lg"
              onClick={() => {
                go(t.inProgress[0] ? `/attempts/${t.inProgress[0].id}` : "/tests");
              }}
            >
              Học tiếp Đề thi →
            </Button>
            <Button
              variant="soft"
              size="lg"
              onClick={() => {
                go("/errors");
              }}
            >
              Trị lỗi sai
            </Button>
          </div>
        </div>
        <div className="hero-mascot">
          {speech && <div className="speech down">{speech}</div>}
          <div className="fishbox">
            <Fish pose={reached ? "party" : undefined} />
          </div>
        </div>
      </section>
      <div className="grid g-7-5">
        <section className="card">
          <div className="card-head">
            <div>
              <div className="row" style={{ gap: 8 }}>
                <SpaceTag space="study" />
                <h2>Kế hoạch tối nay</h2>
              </div>
              <p className="sub">
                Lấy từ Lịch học tuần · {items.length} việc · {planMinutes} phút · bấm ▶ là vào thẳng
                bài, giờ học tính từ lúc bài mở ra
              </p>
            </div>
            <Button
              variant="line"
              size="sm"
              onClick={() => {
                go("/plan");
              }}
            >
              Sửa lịch →
            </Button>
          </div>
          {items.length ? (
            items.map((e, i) => {
              const now = !e.done && i === firstOpen;
              return (
                <div key={e.id} className={`plan-item${e.done ? " done" : ""}${now ? " now" : ""}`}>
                  <div className="pt">
                    {hhmm(e.startMin)}
                    <small>{e.endMin - e.startMin} phút</small>
                  </div>
                  <div>
                    <b>
                      {SKILL_ICON[e.skill] ?? "📌"} {e.title}
                    </b>
                    <div className="pmeta">
                      {SKILL_NAME[e.skill] ?? ""} · {e.endMin - e.startMin} phút
                    </div>
                  </div>
                  {e.done ? (
                    <span className="ok" aria-label="Đã xong">
                      ✓
                    </span>
                  ) : now ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        go(targetFor(e.skill));
                      }}
                    >
                      ▶ Bắt đầu
                    </Button>
                  ) : (
                    <Button
                      variant="soft"
                      size="sm"
                      aria-label={`Bắt đầu ${e.title}`}
                      onClick={() => {
                        go(targetFor(e.skill));
                      }}
                    >
                      ▶
                    </Button>
                  )}
                </div>
              );
            })
          ) : (
            <div className="plan-item">
              <div className="pt faint">—</div>
              <div>
                <b>Chưa có việc nào cho tối nay</b>
                <div className="pmeta">Lên lịch tuần để Cá nhắc và mở thẳng bài đúng giờ.</div>
              </div>
              <Button
                variant="acc"
                size="sm"
                onClick={() => {
                  go("/plan");
                }}
              >
                Lên lịch tuần →
              </Button>
            </div>
          )}
          {!!q.data.unplanned.length && (
            <p className="faint" style={{ fontSize: 12.5, marginTop: 10, fontWeight: 600 }}>
              Ngoài kế hoạch, đã tự ghi:{" "}
              {q.data.unplanned
                .map((u) => {
                  const act = ACT.find((a) => a.id === u.activity);
                  return `${act?.ic ?? ""} ${act?.name ?? ""} ${fmtMin(u.activeSecs / 60)} (${u.at})`;
                })
                .join(" · ")}
            </p>
          )}
        </section>
        <section className="daycard">
          <div className="row between">
            <SpaceTag space="prod" />
            <span
              className="muted"
              style={{ fontSize: 12, fontWeight: 700 }}
              data-tip="Cách tính giờ|Chỉ tính khi bạn đang ở trong một bài; bấm menu hay xem danh sách thì không tính"
            >
              chỉ tính lúc đang làm bài
            </span>
          </div>
          <div className="dc-top">
            <div className="ringbox">
              <Ring pct={(minutes / goal) * 100} sw={11} track="rgba(255,255,255,.14)" />
              <div className="rv">
                <div>
                  <b>{minutes}</b>
                  <small>/ {goal} phút</small>
                </div>
              </div>
            </div>
            <div className="stack grow" style={{ gap: 6 }}>
              <h2 style={{ fontSize: 20, fontWeight: 800 }}>Hôm nay của bạn</h2>
              <p className="muted" style={{ fontSize: 13.5 }}>
                {reached ? "Đã đạt mục tiêu ngày 🎉" : `Còn ${left} phút để đạt mục tiêu ngày`}
              </p>
              <div className="dc-spark" aria-label="Phút học theo giờ trong ngày">
                {Array.from({ length: 18 }, (_, k) => {
                  const h = k + 6,
                    v = hourly[h] ?? 0;
                  return (
                    <i
                      key={h}
                      className={v ? "on" : ""}
                      style={{ height: `${v ? Math.max(18, (v / 60) * 100) : 10}%` }}
                      data-tip={`${h}:00–${h + 1}:00|${v ? fmtMin(v) : "Chưa học"}`}
                    />
                  );
                })}
              </div>
              <div className="row between muted" style={{ fontSize: 10.5, fontWeight: 700 }}>
                <span>6h</span>
                <span>12h</span>
                <span>18h</span>
                <span>24h</span>
              </div>
            </div>
          </div>
          <div className="dc-stats">
            <div>
              <b>
                {focus.data?.rounds.length ?? 0}/{dailyRounds}
              </b>
              <small>hiệp 🍅</small>
            </div>
            <div>
              <b>{streak} ngày</b>
              <small>chuỗi 🔥</small>
            </div>
            <div>
              <b>{answered ? `${Math.round((correct / answered) * 100)}%` : "—"}</b>
              <small>{answered} câu đã làm</small>
            </div>
          </div>
          <Button
            variant="acc"
            onClick={() => {
              go("/report");
            }}
          >
            Xem báo cáo giờ học →
          </Button>
        </section>
      </div>
      <section className="tile p">
        <div className="t-head">
          <div>
            <h3>Thói quen hôm nay</h3>
            <div className="t-sub">
              tự tick từ việc bạn thật sự làm <NewBadge />
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => {
              go("/habits");
            }}
          >
            Mở Thói quen →
          </Button>
        </div>
        <div className="habit-chips">
          {t.habits.length ? (
            t.habits.map((h) =>
              h.automatic ? (
                <span
                  key={h.id}
                  className={`hchip${h.checked ? " on" : ""}`}
                  data-tip={`${h.name}|Cá tự tick khi bạn làm thật`}
                >
                  <span className="box">{h.checked ? "✓" : ""}</span>
                  {h.icon ? `${h.icon} ` : ""}
                  {h.name}
                  {h.checked && <span className="auto">· tự tick</span>}
                </span>
              ) : (
                <button
                  key={h.id}
                  role="checkbox"
                  aria-checked={h.checked}
                  disabled={saving}
                  className={`hchip${h.checked ? " on" : ""}`}
                  data-tip={`${h.name}|Bạn tự tick mỗi ngày`}
                  onClick={() => {
                    setSaving(true);
                    void api(`/v1/me/habits/${h.id}/checks/${t.date}`, {
                      method: "PUT",
                      accountId: uid,
                      body: { checked: !h.checked },
                    })
                      .then(() => q.refetch())
                      .catch((e: unknown) => {
                        notify(String(e));
                      })
                      .finally(() => {
                        setSaving(false);
                      });
                  }}
                >
                  <span className="box">{h.checked ? "✓" : ""}</span>
                  {h.icon ? `${h.icon} ` : ""}
                  {h.name}
                </button>
              ),
            )
          ) : (
            <span className="t-sub">
              Chưa có thói quen nào. Mở Thói quen để thêm thói quen đầu tiên.
            </span>
          )}
        </div>
      </section>
    </>
  );
}
